import React, { useContext, useEffect, useMemo, useState } from "react";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf.js";
import "pdfjs-dist/legacy/build/pdf.worker.entry.js";
import {
  addDoc,
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
  where,
} from "firebase/firestore";
import { getBlob, getDownloadURL, ref } from "firebase/storage";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Stack,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import HistoryIcon from "@mui/icons-material/History";
import PrintIcon from "@mui/icons-material/Print";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import Navbar from "../Components/Navbar/navbar";
import LogoRelatorio from "../Components/LogoRelatorio/LogoRelatorio";
import { AuthContext } from "../Context/auth";
import { db, storage } from "../Config/firebase";
import { aplicarTenant, getUsuarioAtualComTenant, tenantWhere } from "../Utils/tenant";

const COLECAO_TANQUES = "tanques_cura";
const SUBCOLECAO_LEITURAS = "leituras";
const SUBCOLECAO_HISTORICO = "historico";

function calcularCalDoTanque(comprimento, largura, alturaAgua, temperaturaAgua) {
  const valores = [comprimento, largura, alturaAgua, temperaturaAgua].map(Number);
  if (valores.some((valor) => !Number.isFinite(valor))) return null;

  const [comprimentoM, larguraM, alturaAguaM, temperaturaC] = valores;
  const volumeM3 = comprimentoM * larguraM * alturaAguaM;
  const concentracaoGL = (-0.0114 * temperaturaC) + 1.85;

  return {
    volumeM3,
    concentracaoGL,
    quantidadeCalKg: volumeM3 * concentracaoGL,
  };
}

function criarLeituraVazia() {
  return {
    horario: "",
    ambiente: "",
    tanque: "",
  };
}

function criarTresLeiturasVazias() {
  return [criarLeituraVazia(), criarLeituraVazia(), criarLeituraVazia()];
}

function criarRegistroDesvioVazio() {
  return { desvio: "", acao: "", responsavel: "" };
}

function normalizarRegistroDesvio(registro) {
  return {
    desvio: registro?.desvio || "",
    acao: registro?.acao || "",
    responsavel: registro?.responsavel || "",
  };
}

function normalizarHistoricoTermometros(tanque) {
  const historico = Array.isArray(tanque?.termometrosHistorico)
    ? tanque.termometrosHistorico.map((item) => ({
        termometroId: item?.termometroId || "",
        termometroNome: item?.termometroNome || "",
        dataInicio: item?.dataInicio || "",
      }))
    : [];

  if (tanque?.termometroId && !historico.some((item) => item.termometroId === tanque.termometroId)) {
    historico.push({
      termometroId: tanque.termometroId,
      termometroNome: tanque.termometroNome || "",
      dataInicio: tanque.dataInicioTermometro || "",
    });
  }

  return historico;
}

function obterIdsTermometrosNoPeriodo(tanque, inicio, fim) {
  const historico = normalizarHistoricoTermometros(tanque).sort((a, b) =>
    String(a.dataInicio || "0000-00-00").localeCompare(String(b.dataInicio || "0000-00-00"))
  );
  const ids = historico
    .filter((item, index) => {
      const proximoInicio = historico[index + 1]?.dataInicio || "";
      const iniciouAteFim = !item.dataInicio || item.dataInicio <= fim;
      const permaneceuNoPeriodo = !proximoInicio || proximoInicio > inicio;
      return item.termometroId && iniciouAteFim && permaneceuNoPeriodo;
    })
    .map((item) => item.termometroId);

  return [...new Set(ids)];
}

function formatarDataBR(dataISO) {
  if (!dataISO) return "-";
  const [ano, mes, dia] = dataISO.split("-");
  return `${dia}/${mes}/${ano}`;
}

function formatarTemperatura(valor) {
  if (valor === "" || valor === null || valor === undefined || Number.isNaN(Number(valor))) {
    return "";
  }

  return Number(valor).toLocaleString("pt-BR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
}

function normalizarTexto(valor) {
  return String(valor || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

function obterIntervaloDoMes(mesSelecionado) {
  const [ano, mes] = mesSelecionado.split("-").map(Number);
  const inicio = `${ano}-${String(mes).padStart(2, "0")}-01`;
  const ultimoDia = new Date(ano, mes, 0).getDate();
  const fim = `${ano}-${String(mes).padStart(2, "0")}-${String(ultimoDia).padStart(2, "0")}`;
  return { inicio, fim };
}

function normalizarLeituras(leituras) {
  const base = Array.isArray(leituras) ? leituras.slice(0, 3) : [];
  const convertidas = base.map((item) => ({
    horario: item?.horario || "",
    ambiente:
      item?.ambiente === null || item?.ambiente === undefined ? "" : String(item.ambiente),
    tanque: item?.tanque === null || item?.tanque === undefined ? "" : String(item.tanque),
  }));

  while (convertidas.length < 3) {
    convertidas.push(criarLeituraVazia());
  }

  return convertidas;
}

function exibirCampoTabela(valor) {
  if (valor === "" || valor === null || valor === undefined) {
    return "-";
  }

  return valor;
}

function obterUsuarioLogado(usuarioAuth) {
  const nomeLocal = localStorage.getItem("nomeUsuario") || localStorage.getItem("nome") || "";
  const emailLocal = localStorage.getItem("emailUsuario") || "";
  const tipoUsuario = (localStorage.getItem("tipoUsuario") || "").toLowerCase();

  return {
    uid: usuarioAuth?.uid || "",
    nome:
      usuarioAuth?.displayName ||
      nomeLocal ||
      usuarioAuth?.email ||
      "Usuário não identificado",
    email: usuarioAuth?.email || emailLocal || "",
    tipo: tipoUsuario || "nao_informado",
  };
}

function serializarLeituras(leituras) {
  return leituras
    .filter((item) => item.horario && item.ambiente !== "" && item.tanque !== "")
    .map((item) => ({
      horario: item.horario,
      ambiente: Number(item.ambiente),
      tanque: Number(item.tanque),
    }));
}

function SelecaoConforme({ value, onChange, disabled = false }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
      <Stack direction="row" spacing={0.25} alignItems="center">
        <Checkbox checked={value === true} onChange={() => onChange(true)} size="small" disabled={disabled} />
        <Typography variant="body2">Sim</Typography>
      </Stack>
      <Stack direction="row" spacing={0.25} alignItems="center">
        <Checkbox checked={value === false} onChange={() => onChange(false)} size="small" disabled={disabled} />
        <Typography variant="body2">Não</Typography>
      </Stack>
    </Stack>
  );
}

function CamposRegistroDesvio({ data, value, onChange }) {
  const [valorLocal, setValorLocal] = useState(() => normalizarRegistroDesvio(value));

  useEffect(() => {
    setValorLocal(normalizarRegistroDesvio(value));
  }, [value]);

  function atualizar(campo, novoValor) {
    setValorLocal((prev) => ({ ...prev, [campo]: novoValor }));
  }

  function sincronizar() {
    onChange(valorLocal);
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
        Registro de desvio e ação tomada
      </Typography>
      <Stack spacing={1.5}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
          <TextField
            label="Data"
            value={formatarDataBR(data)}
            disabled
            sx={{ width: { xs: "100%", sm: 180 }, flexShrink: 0 }}
          />
          <TextField
            label="Responsável"
            value={valorLocal.responsavel}
            onChange={(e) => atualizar("responsavel", e.target.value)}
            onBlur={sincronizar}
            fullWidth
          />
        </Stack>
        <TextField
          label="Desvio"
          value={valorLocal.desvio}
          onChange={(e) => atualizar("desvio", e.target.value)}
          onBlur={sincronizar}
          multiline
          minRows={2}
          fullWidth
        />
        <TextField
          label="Ação tomada"
          value={valorLocal.acao}
          onChange={(e) => atualizar("acao", e.target.value)}
          onBlur={sincronizar}
          multiline
          minRows={2}
          fullWidth
        />
      </Stack>
    </Paper>
  );
}

export default function ControleTemperaturaTanqueCura({ open, isMobile }) {
  const hoje = new Date();
  const mesAtual = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, "0")}`;
  const dataAtual = hoje.toISOString().slice(0, 10);
  const { usuario: usuarioAuth } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [carregandoLeituras, setCarregandoLeituras] = useState(false);
  const [salvandoTanque, setSalvandoTanque] = useState(false);
  const [salvandoLeituras, setSalvandoLeituras] = useState(false);
  const [salvandoObservacoes, setSalvandoObservacoes] = useState(false);
  const [mensagem, setMensagem] = useState("");
  const [erro, setErro] = useState("");
  const [usuarioTenant, setUsuarioTenant] = useState(null);
  const [relatorioAberto, setRelatorioAberto] = useState(false);
  const [dialogConfigRelatorioAberto, setDialogConfigRelatorioAberto] = useState(false);
  const [tipoPeriodoRelatorio, setTipoPeriodoRelatorio] = useState("mensal");
  const [anoRelatorio, setAnoRelatorio] = useState(String(hoje.getFullYear()));
  const [carregandoRelatorio, setCarregandoRelatorio] = useState(false);
  const [dadosRelatorio, setDadosRelatorio] = useState([]);

  const [tanques, setTanques] = useState([]);
  const [equipamentos, setEquipamentos] = useState([]);
  const [leiturasDoMes, setLeiturasDoMes] = useState([]);
  const [mesSelecionado, setMesSelecionado] = useState(mesAtual);
  const [tanqueSelecionadoId, setTanqueSelecionadoId] = useState("");

  const [dialogTanqueAberto, setDialogTanqueAberto] = useState(false);
  const [dialogFormulasTanqueAberto, setDialogFormulasTanqueAberto] = useState(false);
  const [dialogLeituraAberto, setDialogLeituraAberto] = useState(false);
  const [dialogObservacaoAberto, setDialogObservacaoAberto] = useState(false);
  const [dialogEditarLeiturasAberto, setDialogEditarLeiturasAberto] = useState(false);
  const [dialogHistoricoAberto, setDialogHistoricoAberto] = useState(false);
  const [carregandoHistorico, setCarregandoHistorico] = useState(false);
  const [tanqueEditandoId, setTanqueEditandoId] = useState(null);
  const [buscaTermometro, setBuscaTermometro] = useState("");
  const [buscaNovoTermometro, setBuscaNovoTermometro] = useState("");
  const [adicionandoTermometro, setAdicionandoTermometro] = useState(false);
  const [observacaoSelecionadaData, setObservacaoSelecionadaData] = useState("");
  const [observacaoTexto, setObservacaoTexto] = useState("");
  const [observacaoTextoInicial, setObservacaoTextoInicial] = useState("");
  const [historicoSelecionadoData, setHistoricoSelecionadoData] = useState("");
  const [historicoLeituras, setHistoricoLeituras] = useState([]);
  const [tanqueForm, setTanqueForm] = useState({
    nome: "",
    termometroId: "",
    dataInicioTermometro: dataAtual,
    novoTermometroId: "",
    dataSubstituicao: dataAtual,
    faixaMinima: "",
    faixaMaxima: "",
    comprimento: "",
    largura: "",
    alturaAgua: "",
    temperaturaAgua: "",
  });

  const [formLeituras, setFormLeituras] = useState({
    data: dataAtual,
    leituras: criarTresLeiturasVazias(),
    conforme: null,
    registroDesvio: criarRegistroDesvioVazio(),
  });
  const [formEditarLeituras, setFormEditarLeituras] = useState({
    data: "",
    leituras: criarTresLeiturasVazias(),
    conforme: null,
    registroDesvio: criarRegistroDesvioVazio(),
  });

  const equipamentosOrdenados = useMemo(
    () =>
      [...equipamentos].sort((a, b) =>
        String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR")
      ),
    [equipamentos]
  );

  const termometrosDisponiveis = useMemo(
    () => equipamentosOrdenados,
    [equipamentosOrdenados]
  );

  const tanqueSelecionado = useMemo(
    () => tanques.find((tanque) => tanque.id === tanqueSelecionadoId) || null,
    [tanques, tanqueSelecionadoId]
  );

  const calculoCalTanque = useMemo(
    () => calcularCalDoTanque(
      tanqueForm.comprimento,
      tanqueForm.largura,
      tanqueForm.alturaAgua,
      tanqueForm.temperaturaAgua
    ),
    [
      tanqueForm.comprimento,
      tanqueForm.largura,
      tanqueForm.alturaAgua,
      tanqueForm.temperaturaAgua,
    ]
  );

  const gruposTabela = useMemo(
    () =>
      [...leiturasDoMes].sort((a, b) => String(a.data || "").localeCompare(String(b.data || ""))),
    [leiturasDoMes]
  );
  const grupoLeituraEmEdicao = useMemo(
    () => gruposTabela.find((item) => item.data === formLeituras.data) || null,
    [gruposTabela, formLeituras.data]
  );

  function calcularConformidadeLeituras(leituras) {
    if (!tanqueSelecionado) return null;
    if (tanqueSelecionado.faixaMinima === "" || tanqueSelecionado.faixaMaxima === "") return null;

    const minima = Number(tanqueSelecionado.faixaMinima);
    const maxima = Number(tanqueSelecionado.faixaMaxima);
    if (!Number.isFinite(minima) || !Number.isFinite(maxima)) return null;

    const temperaturas = leituras
      .map((leitura) => leitura?.tanque)
      .filter((temperatura) => temperatura !== "" && temperatura !== null && temperatura !== undefined)
      .map(Number)
      .filter(Number.isFinite);

    if (temperaturas.length === 0) return null;
    return temperaturas.every((temperatura) => temperatura >= minima && temperatura <= maxima);
  }

  useEffect(() => {
    carregarBase();
  }, []);

  useEffect(() => {
    if (!tanqueSelecionadoId) {
      setLeiturasDoMes([]);
      return;
    }

    carregarLeiturasDoMes(tanqueSelecionadoId, mesSelecionado);
  }, [tanqueSelecionadoId, mesSelecionado]);

  async function carregarBase() {
    setLoading(true);

    try {
      const usuarioAtual = await getUsuarioAtualComTenant();
      const [tanquesSnap, equipamentosSnap] = await Promise.all([
        getDocs(query(collection(db, COLECAO_TANQUES), ...tenantWhere(usuarioAtual))),
        getDocs(query(collection(db, "equipamentos"), ...tenantWhere(usuarioAtual))),
      ]);

      const listaTanques = tanquesSnap.docs
        .map((item) => ({ id: item.id, ...item.data() }))
        .sort((a, b) => String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR"));

      const listaEquipamentos = equipamentosSnap.docs
        .map((item) => ({
          id: item.id,
          ...item.data(),
        }))
        .filter((equipamento) => equipamento.ativo !== false);

      setTanques(listaTanques);
      setEquipamentos(listaEquipamentos);
      setUsuarioTenant(usuarioAtual);

      if (!tanqueSelecionadoId && listaTanques.length > 0) {
        setTanqueSelecionadoId(listaTanques[0].id);
      }
    } catch (error) {
      console.error(error);
      setErro("Erro ao carregar os tanques e termômetros.");
    } finally {
      setLoading(false);
    }
  }

  async function abrirRelatorio() {
    if (tanques.length === 0) return;

    setCarregandoRelatorio(true);
    setErro("");
    try {
      const anoAtual = hoje.getFullYear();
      const anoEscolhido = Number(anoRelatorio);
      if (tipoPeriodoRelatorio === "anual" && (!anoEscolhido || anoEscolhido > anoAtual)) {
        throw new Error("ANO_RELATORIO_INVALIDO");
      }

      const mesesRelatorio = tipoPeriodoRelatorio === "anual"
        ? Array.from(
            { length: anoEscolhido === anoAtual ? hoje.getMonth() + 1 : 12 },
            (_, index) => `${anoEscolhido}-${String(index + 1).padStart(2, "0")}`
          )
        : [mesSelecionado];
      const inicio = obterIntervaloDoMes(mesesRelatorio[0]).inicio;
      const fim = obterIntervaloDoMes(mesesRelatorio[mesesRelatorio.length - 1]).fim;
      const documentosSnap = await getDocs(
        query(collection(db, "eq_documentos"), ...tenantWhere(usuarioTenant))
      );
      const certificados = documentosSnap.docs
        .map((item) => ({ id: item.id, ...item.data() }))
        .filter((item) => {
          const tipo = `${item.tipoDocumentoCodigo || ""} ${item.tipoDocumentoNome || ""}`.toUpperCase();
          return item.statusDocumento === "ENVIADO" && item.storagePathAtual && tipo.includes("CERTIFICADO");
        });
      const dados = await Promise.all(
        tanques.slice(0, 3).map(async (tanque) => {
          const leiturasRef = collection(db, COLECAO_TANQUES, tanque.id, SUBCOLECAO_LEITURAS);
          const snap = await getDocs(
            query(leiturasRef, where("data", ">=", inicio), where("data", "<=", fim), orderBy("data", "asc"))
          );
          const todasLeituras = snap.docs.map((item) => ({ id: item.id, ...item.data() }));
          const idsTermometrosPeriodo = obterIdsTermometrosNoPeriodo(tanque, inicio, fim);
          const certificadosDoTanque = await Promise.all(
            certificados
              .filter((certificado) => idsTermometrosPeriodo.includes(certificado.equipamentoId))
              .map(async (certificado) => {
                try {
                  const url = await getDownloadURL(ref(storage, certificado.storagePathAtual));
                  return { ...certificado, url };
                } catch (error) {
                  console.error("Erro ao carregar certificado do termômetro:", error);
                  return null;
                }
              })
          );
          return mesesRelatorio.map((mesDoRelatorio, indexMes) => {
            const intervaloMes = obterIntervaloDoMes(mesDoRelatorio);
            const idsTermometrosMes = obterIdsTermometrosNoPeriodo(
              tanque,
              intervaloMes.inicio,
              intervaloMes.fim
            );
            const nomesTermometros = [
              ...new Set(
                normalizarHistoricoTermometros(tanque)
                  .filter((item) => idsTermometrosMes.includes(item.termometroId))
                  .map((item) => item.termometroNome)
                  .filter(Boolean)
              ),
            ];
            return {
              ...tanque,
              paginaId: `${tanque.id}-${mesDoRelatorio}`,
              mesRelatorio: mesDoRelatorio,
              leiturasMes: todasLeituras.filter((item) =>
                String(item.data || item.id).startsWith(mesDoRelatorio)
              ),
              certificados: indexMes === 0 ? certificadosDoTanque.filter(Boolean) : [],
              nomesTermometros,
            };
          });
        })
      );
      setDadosRelatorio(dados.flat());
      setDialogConfigRelatorioAberto(false);
      setRelatorioAberto(true);
    } catch (error) {
      console.error(error);
      setErro(
        error.message === "ANO_RELATORIO_INVALIDO"
          ? "Selecione um ano válido, igual ou anterior ao ano atual."
          : "Erro ao carregar os dados do relatório."
      );
    } finally {
      setCarregandoRelatorio(false);
    }
  }

  function mudarMes(deslocamento) {
    const [ano, mes] = mesSelecionado.split("-").map(Number);
    const novaData = new Date(ano, mes - 1 + deslocamento, 1);
    setMesSelecionado(
      `${novaData.getFullYear()}-${String(novaData.getMonth() + 1).padStart(2, "0")}`
    );
  }

  async function carregarLeiturasDoMes(tanqueId, mes) {
    setCarregandoLeituras(true);

    try {
      const { inicio, fim } = obterIntervaloDoMes(mes);
      const leiturasRef = collection(db, COLECAO_TANQUES, tanqueId, SUBCOLECAO_LEITURAS);
      const snap = await getDocs(
        query(leiturasRef, where("data", ">=", inicio), where("data", "<=", fim), orderBy("data", "asc"))
      );

      setLeiturasDoMes(
        snap.docs.map((item) => {
          const dados = item.data();
          return {
            id: item.id,
            data: dados.data || item.id,
            observacao: dados.observacao || "",
            conforme: typeof dados.conforme === "boolean" ? dados.conforme : null,
            registroDesvio: normalizarRegistroDesvio(dados.registroDesvio),
            criadoPorNome: dados.criadoPorNome || "",
            criadoPorEmail: dados.criadoPorEmail || "",
            criadoPorUid: dados.criadoPorUid || "",
            atualizadoPorNome: dados.atualizadoPorNome || "",
            atualizadoPorEmail: dados.atualizadoPorEmail || "",
            itens: normalizarLeituras(dados.leituras).map((leitura) => ({
              horario: leitura.horario,
              ambiente: leitura.ambiente === "" ? "" : Number(leitura.ambiente),
              tanque: leitura.tanque === "" ? "" : Number(leitura.tanque),
            })),
          };
        })
      );
    } catch (error) {
      console.error(error);
      setErro("Erro ao carregar as leituras do mês.");
      setLeiturasDoMes([]);
    } finally {
      setCarregandoLeituras(false);
    }
  }

  function abrirNovoTanque() {
    setTanqueEditandoId(null);
    setBuscaTermometro("");
    setBuscaNovoTermometro("");
    setAdicionandoTermometro(false);
    setTanqueForm({
      nome: "",
      termometroId: "",
      dataInicioTermometro: dataAtual,
      novoTermometroId: "",
      dataSubstituicao: dataAtual,
      faixaMinima: "",
      faixaMaxima: "",
      comprimento: "",
      largura: "",
      alturaAgua: "",
      temperaturaAgua: "",
    });
    setDialogTanqueAberto(true);
  }

  function abrirEdicaoTanque(tanque) {
    const historicoTermometros = normalizarHistoricoTermometros(tanque);
    const vinculoAtual = [...historicoTermometros]
      .reverse()
      .find((item) => item.termometroId === tanque.termometroId);
    setTanqueEditandoId(tanque.id);
    setBuscaTermometro(tanque.termometroNome || "");
    setBuscaNovoTermometro("");
    setAdicionandoTermometro(false);
    setTanqueForm({
      nome: tanque.nome || "",
      termometroId: tanque.termometroId || "",
      dataInicioTermometro: vinculoAtual?.dataInicio || tanque.dataInicioTermometro || "",
      novoTermometroId: "",
      dataSubstituicao: dataAtual,
      faixaMinima: tanque.faixaMinima ?? "",
      faixaMaxima: tanque.faixaMaxima ?? "",
      comprimento: tanque.comprimento ?? "",
      largura: tanque.largura ?? "",
      alturaAgua: tanque.alturaAgua ?? "",
      temperaturaAgua: tanque.temperaturaAgua ?? "",
    });
    setDialogTanqueAberto(true);
  }

  function abrirCadastroLeituras() {
    setMensagem("");
    setErro("");
    setFormLeituras({
      data: dataAtual,
      leituras: criarTresLeiturasVazias(),
      conforme: null,
      registroDesvio: criarRegistroDesvioVazio(),
    });
    setDialogLeituraAberto(true);
  }

  useEffect(() => {
    if (!dialogLeituraAberto) return;

    const grupoExistente = gruposTabela.find((item) => item.data === formLeituras.data);
    setFormLeituras((prev) => ({
      ...prev,
      leituras: grupoExistente
        ? normalizarLeituras(grupoExistente.itens)
        : criarTresLeiturasVazias(),
      conforme: calcularConformidadeLeituras(
        grupoExistente ? normalizarLeituras(grupoExistente.itens) : criarTresLeiturasVazias()
      ),
      registroDesvio: normalizarRegistroDesvio(grupoExistente?.registroDesvio),
    }));
  }, [dialogLeituraAberto, formLeituras.data, gruposTabela, tanqueSelecionado]);

  function abrirObservacao(data) {
    const grupo = gruposTabela.find((item) => item.data === data);
    const valorAtual = grupo?.observacao || "";
    setObservacaoSelecionadaData(data);
    setObservacaoTexto(valorAtual);
    setObservacaoTextoInicial(valorAtual);
    setDialogObservacaoAberto(true);
  }

  function abrirEdicaoLeituras(grupo) {
    setFormEditarLeituras({
      data: grupo.data,
      leituras: normalizarLeituras(grupo.itens),
      conforme: calcularConformidadeLeituras(normalizarLeituras(grupo.itens)),
      registroDesvio: normalizarRegistroDesvio(grupo.registroDesvio),
    });
    setDialogEditarLeiturasAberto(true);
  }

  async function abrirHistorico(grupo) {
    if (!tanqueSelecionado) return;

    setHistoricoSelecionadoData(grupo.data);
    setHistoricoLeituras([]);
    setDialogHistoricoAberto(true);
    setCarregandoHistorico(true);
    setErro("");

    try {
      const historicoRef = collection(
        db,
        COLECAO_TANQUES,
        tanqueSelecionado.id,
        SUBCOLECAO_LEITURAS,
        grupo.data,
        SUBCOLECAO_HISTORICO
      );
      const snap = await getDocs(query(historicoRef, orderBy("criadoEm", "desc")));

      setHistoricoLeituras(
        snap.docs.map((item) => {
          const dados = item.data();
          return {
            id: item.id,
            acao: dados.acao || "alteracao",
            usuarioNome: dados.usuarioNome || "Usuário não identificado",
            usuarioEmail: dados.usuarioEmail || "",
            observacao: dados.observacao || "",
            leituras: normalizarLeituras(dados.leituras).map((leitura) => ({
              horario: leitura.horario,
              ambiente: leitura.ambiente === "" ? "" : Number(leitura.ambiente),
              tanque: leitura.tanque === "" ? "" : Number(leitura.tanque),
            })),
            criadoEmTexto: dados.criadoEm?.toDate
              ? dados.criadoEm.toDate().toLocaleDateString("pt-BR")
              : "Data não disponível",
          };
        })
      );
    } catch (error) {
      console.error(error);
      setErro("Erro ao carregar histórico das leituras.");
    } finally {
      setCarregandoHistorico(false);
    }
  }

  function atualizarLeitura(index, campo, valor) {
    setFormLeituras((prev) => {
      const prox = [...prev.leituras];
      prox[index] = {
        ...(prox[index] || criarLeituraVazia()),
        [campo]: valor,
      };

      return {
        ...prev,
        leituras: prox,
        conforme: calcularConformidadeLeituras(prox),
      };
    });
  }

  function atualizarLeituraEdicao(index, campo, valor) {
    setFormEditarLeituras((prev) => {
      const prox = [...prev.leituras];
      prox[index] = {
        ...(prox[index] || criarLeituraVazia()),
        [campo]: valor,
      };

      return {
        ...prev,
        leituras: prox,
        conforme: calcularConformidadeLeituras(prox),
      };
    });
  }

  function atualizarGrupoNaTabela(data, payload) {
    setLeiturasDoMes((prev) => {
      const proximoGrupo = {
        id: data,
        data,
        observacao: payload.observacao || "",
        conforme: typeof payload.conforme === "boolean" ? payload.conforme : null,
        registroDesvio: normalizarRegistroDesvio(payload.registroDesvio),
        criadoPorNome: payload.criadoPorNome || "",
        criadoPorEmail: payload.criadoPorEmail || "",
        criadoPorUid: payload.criadoPorUid || "",
        atualizadoPorNome: payload.atualizadoPorNome || "",
        atualizadoPorEmail: payload.atualizadoPorEmail || "",
        itens: normalizarLeituras(payload.leituras).map((leitura) => ({
          horario: leitura.horario,
          ambiente: leitura.ambiente === "" ? "" : Number(leitura.ambiente),
          tanque: leitura.tanque === "" ? "" : Number(leitura.tanque),
        })),
      };

      const existe = prev.some((item) => item.data === data);
      const atualizados = existe
        ? prev.map((item) => (item.data === data ? { ...item, ...proximoGrupo } : item))
        : [...prev, proximoGrupo];

      return atualizados.sort((a, b) => String(a.data || "").localeCompare(String(b.data || "")));
    });
  }

  async function salvarTanque() {
    const substituindoTermometro = Boolean(tanqueEditandoId && adicionandoTermometro);
    const termometroIdFinal = substituindoTermometro
      ? tanqueForm.novoTermometroId
      : tanqueForm.termometroId;
    const dataInicioFinal = substituindoTermometro
      ? tanqueForm.dataSubstituicao
      : tanqueForm.dataInicioTermometro;

    if (
      !tanqueForm.nome.trim() ||
      !termometroIdFinal ||
      (!tanqueEditandoId && !dataInicioFinal) ||
      (substituindoTermometro && !dataInicioFinal) ||
      tanqueForm.faixaMinima === "" ||
      tanqueForm.faixaMaxima === "" ||
      tanqueForm.comprimento === "" ||
      tanqueForm.largura === "" ||
      tanqueForm.alturaAgua === "" ||
      tanqueForm.temperaturaAgua === ""
    ) {
      setErro("Informe o nome, o termômetro, a data de início do uso e a faixa do tanque.");
      return;
    }

    const dimensoes = [tanqueForm.comprimento, tanqueForm.largura, tanqueForm.alturaAgua].map(Number);
    const temperaturaAgua = Number(tanqueForm.temperaturaAgua);
    if (dimensoes.some((valor) => !Number.isFinite(valor) || valor <= 0)) {
      setErro("Informe dimensões e altura da água maiores que zero.");
      return;
    }
    if (!Number.isFinite(temperaturaAgua) || temperaturaAgua < 0 || temperaturaAgua > 100) {
      setErro("A temperatura da água para o cálculo deve estar entre 0 e 100 °C.");
      return;
    }

    if (
      !Number.isFinite(Number(tanqueForm.faixaMinima)) ||
      !Number.isFinite(Number(tanqueForm.faixaMaxima))
    ) {
      setErro("Informe valores válidos para a faixa de temperatura.");
      return;
    }

    if (Number(tanqueForm.faixaMinima) > Number(tanqueForm.faixaMaxima)) {
      setErro("A temperatura mínima da faixa não pode ser maior que a máxima.");
      return;
    }

    const termometro = equipamentos.find(
      (equipamento) => equipamento.id === termometroIdFinal
    );

    if (!termometro) {
      setErro("Selecione um termômetro válido.");
      return;
    }

    setSalvandoTanque(true);
    setErro("");
    setMensagem("");

    const tanqueOriginal = tanques.find((tanque) => tanque.id === tanqueEditandoId) || null;
    const termometrosHistorico = normalizarHistoricoTermometros(tanqueOriginal);
    const houveSubstituicao =
      substituindoTermometro &&
      Boolean(tanqueOriginal?.termometroId) &&
      tanqueOriginal.termometroId !== termometro.id;
    const corrigindoTermometroAtual =
      Boolean(tanqueOriginal?.termometroId) &&
      !substituindoTermometro &&
      tanqueOriginal.termometroId !== termometro.id;

    if (substituindoTermometro && !houveSubstituicao) {
      setErro("Selecione um termômetro diferente do equipamento atual.");
      setSalvandoTanque(false);
      return;
    }

    if (houveSubstituicao) {
      const datasOrdenadas = termometrosHistorico
        .map((item) => item.dataInicio)
        .filter(Boolean)
        .sort();
      const ultimaData = datasOrdenadas[datasOrdenadas.length - 1];
      if (ultimaData && dataInicioFinal <= ultimaData) {
        setErro("A data da substituição deve ser posterior ao início do termômetro anterior.");
        setSalvandoTanque(false);
        return;
      }
    }

    if (houveSubstituicao || termometrosHistorico.length === 0) {
      termometrosHistorico.push({
        termometroId: termometro.id,
        termometroNome: termometro.nome || "",
        dataInicio: dataInicioFinal,
      });
    } else {
      let indiceAtual = -1;
      const idVinculoAtual = corrigindoTermometroAtual
        ? tanqueOriginal.termometroId
        : termometro.id;
      for (let index = termometrosHistorico.length - 1; index >= 0; index -= 1) {
        if (termometrosHistorico[index].termometroId === idVinculoAtual) {
          indiceAtual = index;
          break;
        }
      }
      if (indiceAtual >= 0) {
        termometrosHistorico[indiceAtual] = {
          termometroId: termometro.id,
          termometroNome: termometro.nome || "",
          dataInicio: dataInicioFinal,
        };
      }
    }

    const payload = aplicarTenant({
      nome: tanqueForm.nome.trim(),
      termometroId: termometro.id,
      termometroNome: termometro.nome || "",
      dataInicioTermometro: dataInicioFinal,
      termometrosHistorico,
      faixaMinima: Number(tanqueForm.faixaMinima),
      faixaMaxima: Number(tanqueForm.faixaMaxima),
      comprimento: Number(tanqueForm.comprimento),
      largura: Number(tanqueForm.largura),
      alturaAgua: Number(tanqueForm.alturaAgua),
      temperaturaAgua,
      volumeM3: calculoCalTanque.volumeM3,
      concentracaoCalGL: calculoCalTanque.concentracaoGL,
      quantidadeCalKg: calculoCalTanque.quantidadeCalKg,
      atualizadoEm: serverTimestamp(),
      ativo: true,
    }, usuarioTenant);

    try {
      let tanqueIdFinal = tanqueEditandoId;

      if (tanqueEditandoId) {
        await updateDoc(doc(db, COLECAO_TANQUES, tanqueEditandoId), payload);
      } else {
        const novoTanque = await addDoc(collection(db, COLECAO_TANQUES), {
          ...payload,
          criadoEm: serverTimestamp(),
        });
        tanqueIdFinal = novoTanque.id;
      }

      setDialogTanqueAberto(false);
      setTanqueSelecionadoId(tanqueIdFinal || "");
      setMensagem(
        tanqueEditandoId
          ? "Tanque atualizado com sucesso!"
          : "Tanque cadastrado com sucesso!"
      );
      await carregarBase();
    } catch (error) {
      console.error(error);
      setErro("Erro ao salvar tanque.");
    } finally {
      setSalvandoTanque(false);
    }
  }

  async function salvarLeituras() {
    if (!tanqueSelecionado) {
      setErro("Selecione um tanque.");
      return;
    }

    const leiturasValidas = serializarLeituras(formLeituras.leituras);

    if (!formLeituras.data || leiturasValidas.length === 0) {
      setErro("Preencha a data e pelo menos uma leitura.");
      return;
    }

    if (typeof formLeituras.conforme !== "boolean") {
      setErro("Informe se as leituras do dia estão conformes.");
      return;
    }

    if (
      formLeituras.conforme === false &&
      (!formLeituras.registroDesvio.desvio.trim() ||
        !formLeituras.registroDesvio.acao.trim() ||
        !formLeituras.registroDesvio.responsavel.trim())
    ) {
      setErro("Preencha o desvio, a ação tomada e o responsável.");
      return;
    }

    setSalvandoLeituras(true);
    setErro("");
    setMensagem("");

    try {
      const usuarioLogado = obterUsuarioLogado(usuarioAuth);
      const grupoExistente = gruposTabela.find((item) => item.data === formLeituras.data);
      const payload = {
        data: formLeituras.data,
        observacao: grupoExistente?.observacao || "",
        conforme: formLeituras.conforme,
        registroDesvio: formLeituras.conforme
          ? null
          : {
              desvio: formLeituras.registroDesvio.desvio.trim(),
              acao: formLeituras.registroDesvio.acao.trim(),
              responsavel: formLeituras.registroDesvio.responsavel.trim(),
            },
        leituras: leiturasValidas,
        atualizadoEm: serverTimestamp(),
        atualizadoPorUid: usuarioLogado.uid,
        atualizadoPorNome: usuarioLogado.nome,
        atualizadoPorEmail: usuarioLogado.email,
        atualizadoPorTipo: usuarioLogado.tipo,
      };

      if (!grupoExistente?.criadoPorUid) {
        payload.criadoEm = serverTimestamp();
        payload.criadoPorUid = usuarioLogado.uid;
        payload.criadoPorNome = usuarioLogado.nome;
        payload.criadoPorEmail = usuarioLogado.email;
        payload.criadoPorTipo = usuarioLogado.tipo;
      }

      const leituraRef = doc(
        db,
        COLECAO_TANQUES,
        tanqueSelecionado.id,
        SUBCOLECAO_LEITURAS,
        formLeituras.data
      );
      const historicoRef = doc(
        collection(
          db,
          COLECAO_TANQUES,
          tanqueSelecionado.id,
          SUBCOLECAO_LEITURAS,
          formLeituras.data,
          SUBCOLECAO_HISTORICO
        )
      );
      const batch = writeBatch(db);
      batch.set(leituraRef, payload, { merge: true });
      batch.set(historicoRef, {
        acao: grupoExistente ? "edicao_leituras" : "cadastro_leituras",
        data: formLeituras.data,
        observacao: grupoExistente?.observacao || "",
        conforme: formLeituras.conforme,
        registroDesvio: payload.registroDesvio,
        leituras: leiturasValidas,
        usuarioUid: usuarioLogado.uid,
        usuarioNome: usuarioLogado.nome,
        usuarioEmail: usuarioLogado.email,
        usuarioTipo: usuarioLogado.tipo,
        criadoEm: serverTimestamp(),
      });
      await batch.commit();

      atualizarGrupoNaTabela(formLeituras.data, payload);
      setDialogLeituraAberto(false);
      setMesSelecionado(formLeituras.data.slice(0, 7));
      setMensagem(`Leituras registradas para ${tanqueSelecionado.nome}.`);
      await carregarLeiturasDoMes(tanqueSelecionado.id, formLeituras.data.slice(0, 7));
    } catch (error) {
      console.error(error);
      setErro("Erro ao salvar leituras do tanque.");
    } finally {
      setSalvandoLeituras(false);
    }
  }

  async function salvarObservacoes(data) {
    if (!tanqueSelecionado) return;

    setSalvandoObservacoes(true);
    setErro("");
    setMensagem("");

    try {
      const usuarioLogado = obterUsuarioLogado(usuarioAuth);
      const grupoExistente = gruposTabela.find((item) => item.data === data);
      const leiturasValidas = serializarLeituras(grupoExistente?.itens || []);
      const payload = {
        data,
        observacao: observacaoTexto.trim(),
        leituras: leiturasValidas,
        atualizadoEm: serverTimestamp(),
        atualizadoPorUid: usuarioLogado.uid,
        atualizadoPorNome: usuarioLogado.nome,
        atualizadoPorEmail: usuarioLogado.email,
        atualizadoPorTipo: usuarioLogado.tipo,
      };

      if (!grupoExistente?.criadoPorUid) {
        payload.criadoEm = serverTimestamp();
        payload.criadoPorUid = usuarioLogado.uid;
        payload.criadoPorNome = usuarioLogado.nome;
        payload.criadoPorEmail = usuarioLogado.email;
        payload.criadoPorTipo = usuarioLogado.tipo;
      }

      const leituraRef = doc(db, COLECAO_TANQUES, tanqueSelecionado.id, SUBCOLECAO_LEITURAS, data);
      const historicoRef = doc(
        collection(
          db,
          COLECAO_TANQUES,
          tanqueSelecionado.id,
          SUBCOLECAO_LEITURAS,
          data,
          SUBCOLECAO_HISTORICO
        )
      );
      const batch = writeBatch(db);
      batch.set(leituraRef, payload, { merge: true });
      batch.set(historicoRef, {
        acao: "edicao_observacao",
        data,
        observacao: observacaoTexto.trim(),
        leituras: leiturasValidas,
        usuarioUid: usuarioLogado.uid,
        usuarioNome: usuarioLogado.nome,
        usuarioEmail: usuarioLogado.email,
        usuarioTipo: usuarioLogado.tipo,
        criadoEm: serverTimestamp(),
      });
      await batch.commit();

      setObservacaoTextoInicial(observacaoTexto.trim());
      atualizarGrupoNaTabela(data, payload);
      setMensagem(
        `Observações salvas para ${tanqueSelecionado.nome} em ${formatarDataBR(data)}.`
      );
      setDialogObservacaoAberto(false);
      await carregarLeiturasDoMes(tanqueSelecionado.id, mesSelecionado);
    } catch (error) {
      console.error(error);
      setErro("Erro ao salvar observações.");
    } finally {
      setSalvandoObservacoes(false);
    }
  }

  async function salvarEdicaoLeituras() {
    if (!tanqueSelecionado || !formEditarLeituras.data) return;

    const leiturasValidas = serializarLeituras(formEditarLeituras.leituras);

    if (leiturasValidas.length === 0) {
      setErro("Preencha pelo menos uma leitura válida.");
      return;
    }

    if (typeof formEditarLeituras.conforme !== "boolean") {
      setErro("Informe se as leituras do dia estão conformes.");
      return;
    }

    if (
      formEditarLeituras.conforme === false &&
      (!formEditarLeituras.registroDesvio.desvio.trim() ||
        !formEditarLeituras.registroDesvio.acao.trim() ||
        !formEditarLeituras.registroDesvio.responsavel.trim())
    ) {
      setErro("Preencha o desvio, a ação tomada e o responsável.");
      return;
    }

    setSalvandoLeituras(true);
    setErro("");
    setMensagem("");

    try {
      const usuarioLogado = obterUsuarioLogado(usuarioAuth);
      const grupoExistente = gruposTabela.find((item) => item.data === formEditarLeituras.data);
      const payload = {
        data: formEditarLeituras.data,
        observacao: grupoExistente?.observacao || "",
        conforme: formEditarLeituras.conforme,
        registroDesvio: formEditarLeituras.conforme
          ? null
          : {
              desvio: formEditarLeituras.registroDesvio.desvio.trim(),
              acao: formEditarLeituras.registroDesvio.acao.trim(),
              responsavel: formEditarLeituras.registroDesvio.responsavel.trim(),
            },
        leituras: leiturasValidas,
        atualizadoEm: serverTimestamp(),
        atualizadoPorUid: usuarioLogado.uid,
        atualizadoPorNome: usuarioLogado.nome,
        atualizadoPorEmail: usuarioLogado.email,
        atualizadoPorTipo: usuarioLogado.tipo,
      };

      if (!grupoExistente?.criadoPorUid) {
        payload.criadoEm = serverTimestamp();
        payload.criadoPorUid = usuarioLogado.uid;
        payload.criadoPorNome = usuarioLogado.nome;
        payload.criadoPorEmail = usuarioLogado.email;
        payload.criadoPorTipo = usuarioLogado.tipo;
      }

      const leituraRef = doc(
        db,
        COLECAO_TANQUES,
        tanqueSelecionado.id,
        SUBCOLECAO_LEITURAS,
        formEditarLeituras.data
      );
      const historicoRef = doc(
        collection(
          db,
          COLECAO_TANQUES,
          tanqueSelecionado.id,
          SUBCOLECAO_LEITURAS,
          formEditarLeituras.data,
          SUBCOLECAO_HISTORICO
        )
      );
      const batch = writeBatch(db);
      batch.set(leituraRef, payload, { merge: true });
      batch.set(historicoRef, {
        acao: "edicao_leituras",
        data: formEditarLeituras.data,
        observacao: grupoExistente?.observacao || "",
        conforme: formEditarLeituras.conforme,
        registroDesvio: payload.registroDesvio,
        leituras: leiturasValidas,
        usuarioUid: usuarioLogado.uid,
        usuarioNome: usuarioLogado.nome,
        usuarioEmail: usuarioLogado.email,
        usuarioTipo: usuarioLogado.tipo,
        criadoEm: serverTimestamp(),
      });
      await batch.commit();

      atualizarGrupoNaTabela(formEditarLeituras.data, payload);
      setDialogEditarLeiturasAberto(false);
      setMensagem(
        `Leituras atualizadas para ${tanqueSelecionado.nome} em ${formatarDataBR(
          formEditarLeituras.data
        )}.`
      );
      await carregarLeiturasDoMes(tanqueSelecionado.id, formEditarLeituras.data.slice(0, 7));
    } catch (error) {
      console.error(error);
      setErro("Erro ao atualizar leituras.");
    } finally {
      setSalvandoLeituras(false);
    }
  }

  if (relatorioAberto) {
    return (
      <RelatorioTanquesCura
        tanques={dadosRelatorio}
        onVoltar={() => setRelatorioAberto(false)}
      />
    );
  }

  return (
    <Box>
      <Navbar open={open} isMobile={isMobile} />

      <Box sx={{ p: 3, mt: "3px", backgroundColor: "#f4f6f8", minHeight: "100vh" }}>
        <Paper sx={{ p: 2, borderRadius: 2, mb: 2 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
            spacing={2}
          >
            <Stack direction={{ xs: "column", lg: "row" }} spacing={2} sx={{ flex: 1 }}>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="overline"
                  sx={{ color: "#1976d2", fontWeight: 700, display: "block" }}
                >
                  Sistema de Gestão da Qualidade
                </Typography>
                <Typography variant="h5" fontWeight={700}>
                  Controle de Temperatura - Tanque de Cura dos Corpos de Prova NBR 9479
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Selecione um tanque para visualizar as leituras cadastradas do mês.
                </Typography>
              </Box>

              <Box
                sx={{
                  minWidth: 150,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Typography variant="body2" fontWeight={700}>
                  RQ 4.2.1
                </Typography>
                <Typography variant="body2">Revisão 00</Typography>
                <Typography variant="body2">20/08/2025</Typography>
              </Box>
            </Stack>
          </Stack>
        </Paper>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          sx={{ mb: 2 }}
        >
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip title="Mês anterior">
              <IconButton onClick={() => mudarMes(-1)} aria-label="Mês anterior">
                <ChevronLeftIcon />
              </IconButton>
            </Tooltip>
            <TextField
              label="Mês"
              type="month"
              size="small"
              value={mesSelecionado}
              onChange={(e) => setMesSelecionado(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: { xs: "100%", sm: 180 } }}
            />
            <Tooltip title="Próximo mês">
              <IconButton onClick={() => mudarMes(1)} aria-label="Próximo mês">
                <ChevronRightIcon />
              </IconButton>
            </Tooltip>
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button
              variant="outlined"
              startIcon={carregandoRelatorio ? <CircularProgress size={18} /> : <PrintIcon />}
              onClick={() => {
                setAnoRelatorio(mesSelecionado.slice(0, 4));
                setDialogConfigRelatorioAberto(true);
              }}
              disabled={loading || carregandoRelatorio || tanques.length === 0}
            >
              Relatórios dos tanques
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={abrirNovoTanque}
              sx={{ alignSelf: { xs: "stretch", sm: "center" } }}
            >
              Novo Tanque
            </Button>
          </Stack>
        </Stack>

        {(mensagem || erro) && (
          <Stack spacing={1} sx={{ mb: 2 }}>
            {mensagem && <Alert severity="success">{mensagem}</Alert>}
            {erro && <Alert severity="error">{erro}</Alert>}
          </Stack>
        )}

        {loading ? (
          <Box sx={{ py: 8, display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Paper sx={{ p: 2.5, borderRadius: 2, mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
                Tanques cadastrados
              </Typography>

              {tanques.length === 0 ? (
                <Typography color="text.secondary">
                  Nenhum tanque cadastrado. Use o botão "Novo Tanque" para começar.
                </Typography>
              ) : (
                <Stack spacing={1.2}>
                  {tanques.map((tanque) => (
                    <Paper
                      key={tanque.id}
                      variant="outlined"
                      onClick={() => setTanqueSelecionadoId(tanque.id)}
                      sx={{
                        p: 1.8,
                        cursor: "pointer",
                        borderColor:
                          tanqueSelecionadoId === tanque.id ? "#1976d2" : "#d9dee7",
                        backgroundColor:
                          tanqueSelecionadoId === tanque.id ? "#eef5ff" : "#fff",
                      }}
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        spacing={1}
                      >
                        <Box>
                          <Typography fontWeight={700}>{tanque.nome}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Termômetro: {tanque.termometroNome || "Não vinculado"}
                          </Typography>
                          {Number.isFinite(Number(tanque.quantidadeCalKg)) && (
                            <Typography variant="body2" color="text.secondary">
                              Volume: {Number(tanque.volumeM3).toFixed(3)} m³ | Cal: {Number(tanque.quantidadeCalKg).toFixed(2)} kg a {Number(tanque.temperaturaAgua).toFixed(1)} °C
                            </Typography>
                          )}
                        </Box>

                        <Button
                          size="small"
                          onClick={(event) => {
                            event.stopPropagation();
                            abrirEdicaoTanque(tanque);
                          }}
                        >
                          Editar
                        </Button>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              )}
            </Paper>
            {tanqueSelecionado && (
              <Paper sx={{ p: 2.5, borderRadius: 2 }}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  spacing={1.5}
                  sx={{ mb: 2 }}
                >
                  <Box>
                    <Button
                      size="small"
                      startIcon={<ArrowBackIcon />}
                      sx={{ mb: 1, pl: 0 }}
                      onClick={() => setTanqueSelecionadoId("")}
                    >
                      Voltar para lista
                    </Button>
                    <Typography variant="h6" fontWeight={700}>
                      {tanqueSelecionado.nome}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Termômetro: {tanqueSelecionado.termometroNome || "Não vinculado"}
                    </Typography>
                  </Box>

                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={abrirCadastroLeituras}
                  >
                    Cadastrar leitura
                  </Button>
                </Stack>

                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                  Leituras do mês
                </Typography>

                {carregandoLeituras ? (
                  <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
                    <CircularProgress size={28} />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      width: "100%",
                      overflowX: "auto",
                      overflowY: "auto",
                      WebkitOverflowScrolling: "touch",
                      border: "1px solid #d9dee7",
                      borderRadius: 1,
                      maxHeight: { xs: "65vh", md: "none" },
                    }}
                  >
                    <Table
                      size="small"
                      sx={{
                        minWidth: 1180,
                        borderCollapse: "collapse",
                        "& th, & td": {
                          border: "1px solid #d9dee7",
                          py: 0.75,
                          px: 1,
                          verticalAlign: "middle",
                          whiteSpace: "nowrap",
                        },
                      }}
                    >
                    <TableHead>
                      <TableRow>
                        <TableCell align="center" sx={{ width: 140 }} rowSpan={2}>
                          DATA
                        </TableCell>
                        <TableCell align="center" sx={{ width: 120 }} rowSpan={2}>
                          HORÁRIO
                        </TableCell>
                        <TableCell colSpan={2} align="center">
                          TEMPERATURA (°C)
                        </TableCell>
                        <TableCell align="center" sx={{ width: 120 }} rowSpan={2}>
                          HORÁRIO
                        </TableCell>
                        <TableCell colSpan={2} align="center">
                          TEMPERATURA (°C)
                        </TableCell>
                        <TableCell align="center" sx={{ width: 120 }} rowSpan={2}>
                          HORÁRIO
                        </TableCell>
                        <TableCell colSpan={2} align="center">
                          TEMPERATURA (°C)
                        </TableCell>
                        <TableCell align="center" sx={{ width: 90 }} rowSpan={2}>
                          CONFORME
                        </TableCell>
                        <TableCell align="center" sx={{ width: 130 }} rowSpan={2}>
                          AÇÕES
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell align="center">AMBIENTE</TableCell>
                        <TableCell align="center">TANQUE</TableCell>
                        <TableCell align="center">AMBIENTE</TableCell>
                        <TableCell align="center">TANQUE</TableCell>
                        <TableCell align="center">AMBIENTE</TableCell>
                        <TableCell align="center">TANQUE</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {gruposTabela.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={12} align="center">
                            Nenhuma leitura cadastrada para este tanque no mês selecionado.
                          </TableCell>
                        </TableRow>
                      )}

                      {gruposTabela.map((grupo) => {
                        const leituras = [...grupo.itens].slice(0, 3);

                        while (leituras.length < 3) {
                          leituras.push(null);
                        }

                        return (
                          <TableRow key={grupo.data}>
                            <TableCell align="center">{formatarDataBR(grupo.data)}</TableCell>
                            <TableCell align="center">
                              {exibirCampoTabela(leituras[0]?.horario)}
                            </TableCell>
                            <TableCell align="center">
                              {exibirCampoTabela(formatarTemperatura(leituras[0]?.ambiente))}
                            </TableCell>
                            <TableCell align="center">
                              {exibirCampoTabela(formatarTemperatura(leituras[0]?.tanque))}
                            </TableCell>
                            <TableCell align="center">
                              {exibirCampoTabela(leituras[1]?.horario)}
                            </TableCell>
                            <TableCell align="center">
                              {exibirCampoTabela(formatarTemperatura(leituras[1]?.ambiente))}
                            </TableCell>
                            <TableCell align="center">
                              {exibirCampoTabela(formatarTemperatura(leituras[1]?.tanque))}
                            </TableCell>
                            <TableCell align="center">
                              {exibirCampoTabela(leituras[2]?.horario)}
                            </TableCell>
                            <TableCell align="center">
                              {exibirCampoTabela(formatarTemperatura(leituras[2]?.ambiente))}
                            </TableCell>
                            <TableCell align="center">
                              {exibirCampoTabela(formatarTemperatura(leituras[2]?.tanque))}
                            </TableCell>
                            <TableCell align="center">
                              {grupo.conforme === true
                                ? "SIM"
                                : grupo.conforme === false
                                  ? "NÃO"
                                  : "-"}
                            </TableCell>
                            <TableCell align="center">
                              <Stack direction="row" spacing={0.5} justifyContent="center">
                                <Tooltip title="Editar leituras do dia">
                                  <IconButton
                                    color="primary"
                                    onClick={() => abrirEdicaoLeituras(grupo)}
                                  >
                                    <EditIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Ver histórico do dia">
                                  <IconButton
                                    color="default"
                                    onClick={() => abrirHistorico(grupo)}
                                  >
                                    <HistoryIcon />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                    </Table>
                  </Box>
                )}
              </Paper>
            )}
          </>
        )}
      </Box>

      <Dialog
        open={dialogConfigRelatorioAberto}
        onClose={() => !carregandoRelatorio && setDialogConfigRelatorioAberto(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Configurar período do relatório</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel id="tipo-periodo-relatorio-label">Período</InputLabel>
              <Select
                labelId="tipo-periodo-relatorio-label"
                label="Período"
                value={tipoPeriodoRelatorio}
                onChange={(e) => setTipoPeriodoRelatorio(e.target.value)}
              >
                <MenuItem value="mensal">Mensal</MenuItem>
                <MenuItem value="anual">Anual</MenuItem>
              </Select>
            </FormControl>
            {tipoPeriodoRelatorio === "mensal" ? (
              <TextField
                label="Mês"
                type="month"
                value={mesSelecionado}
                onChange={(e) => setMesSelecionado(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            ) : (
              <TextField
                label="Ano"
                type="number"
                value={anoRelatorio}
                onChange={(e) => setAnoRelatorio(e.target.value)}
                inputProps={{ min: 2000, max: hoje.getFullYear(), step: 1 }}
                helperText={
                  Number(anoRelatorio) === hoje.getFullYear()
                    ? "Será gerado de janeiro até o mês vigente."
                    : "Para anos anteriores, será gerado de janeiro a dezembro."
                }
                fullWidth
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogConfigRelatorioAberto(false)} disabled={carregandoRelatorio}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={abrirRelatorio} disabled={carregandoRelatorio}>
            {carregandoRelatorio ? "Gerando..." : "Gerar relatório"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={dialogObservacaoAberto}
        onClose={() => !salvandoObservacoes && setDialogObservacaoAberto(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{"Observação de " + formatarDataBR(observacaoSelecionadaData)}</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            multiline
            minRows={4}
            value={observacaoTexto}
            onChange={(e) => setObservacaoTexto(e.target.value)}
            placeholder={
              "Registre a observação de " + formatarDataBR(observacaoSelecionadaData) + "..."
            }
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setObservacaoTexto(observacaoTextoInicial);
              setDialogObservacaoAberto(false);
            }}
            disabled={salvandoObservacoes}
          >
            Cancelar
          </Button>
          {observacaoTexto !== observacaoTextoInicial && (
            <Button
              variant="contained"
              onClick={() => salvarObservacoes(observacaoSelecionadaData)}
              disabled={salvandoObservacoes}
            >
              {salvandoObservacoes ? "Salvando..." : "Salvar"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={dialogHistoricoAberto}
        onClose={() => !carregandoHistorico && setDialogHistoricoAberto(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{"Histórico de " + formatarDataBR(historicoSelecionadoData)}</DialogTitle>
        <DialogContent dividers>
          {carregandoHistorico ? (
            <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
              <CircularProgress size={28} />
            </Box>
          ) : historicoLeituras.length === 0 ? (
            <Typography color="text.secondary">
              Nenhum histórico encontrado para esta data.
            </Typography>
          ) : (
            <Stack spacing={2}>
              {historicoLeituras.map((item) => (
                <Paper key={item.id} variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={1.5}>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      justifyContent="space-between"
                      spacing={1}
                    >
                      <Box>
                        <Typography fontWeight={700}>
                          {item.acao === "cadastro_leituras"
                            ? "Cadastro inicial"
                            : item.acao === "edicao_observacao"
                              ? "Alteração de observação"
                              : "Alteração de leituras"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Responsável: {item.usuarioEmail || item.usuarioNome || "Usuário não identificado"}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {item.criadoEmTexto}
                      </Typography>
                    </Stack>

                    <Table
                      size="small"
                      sx={{
                        border: "1px solid #d9dee7",
                        "& th, & td": {
                          border: "1px solid #d9dee7",
                          py: 0.75,
                        },
                      }}
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell align="center">HORÁRIO</TableCell>
                          <TableCell align="center">AMBIENTE (°C)</TableCell>
                          <TableCell align="center">TANQUE (°C)</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {item.leituras.map((leitura, index) => (
                          <TableRow key={`${item.id}-${index}`}>
                            <TableCell align="center">
                              {exibirCampoTabela(leitura.horario)}
                            </TableCell>
                            <TableCell align="center">
                              {exibirCampoTabela(formatarTemperatura(leitura.ambiente))}
                            </TableCell>
                            <TableCell align="center">
                              {exibirCampoTabela(formatarTemperatura(leitura.tanque))}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>

                    <Box>
                      <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
                        Observação
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {item.observacao?.trim() || "-"}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogHistoricoAberto(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={dialogEditarLeiturasAberto}
        onClose={() => !salvandoLeituras && setDialogEditarLeiturasAberto(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{"Editar leituras de " + formatarDataBR(formEditarLeituras.data)}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              label="Data"
              type="date"
              size="small"
              value={formEditarLeituras.data}
              onChange={(e) =>
                setFormEditarLeituras((prev) => ({ ...prev, data: e.target.value }))
              }
              InputLabelProps={{ shrink: true }}
              sx={{ width: 180 }}
            />

            <Table
              size="small"
              sx={{
                border: "1px solid #d9dee7",
                "& th, & td": {
                  border: "1px solid #d9dee7",
                  py: 0.75,
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell align="center">HORÁRIO</TableCell>
                  <TableCell align="center">AMBIENTE (°C)</TableCell>
                  <TableCell align="center">TANQUE (°C)</TableCell>
                  <TableCell align="center">CONFORME</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {formEditarLeituras.leituras.map((leitura, index) => (
                  <TableRow key={`edicao-${index}`}>
                    <TableCell>
                      <TextField
                        type="time"
                        size="small"
                        value={leitura.horario}
                        onChange={(e) => atualizarLeituraEdicao(index, "horario", e.target.value)}
                        fullWidth
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={leitura.ambiente}
                        onChange={(e) => atualizarLeituraEdicao(index, "ambiente", e.target.value)}
                        fullWidth
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={leitura.tanque}
                        onChange={(e) => atualizarLeituraEdicao(index, "tanque", e.target.value)}
                        fullWidth
                      />
                    </TableCell>
                    {index === 0 && (
                      <TableCell rowSpan={3} align="center">
                        <SelecaoConforme
                          value={formEditarLeituras.conforme}
                          onChange={() => {}}
                          disabled
                        />
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {formEditarLeituras.conforme === false && (
              <CamposRegistroDesvio
                data={formEditarLeituras.data}
                value={formEditarLeituras.registroDesvio}
                onChange={(registroDesvio) =>
                  setFormEditarLeituras((prev) => ({ ...prev, registroDesvio }))
                }
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDialogEditarLeiturasAberto(false)}
            disabled={salvandoLeituras}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={salvarEdicaoLeituras}
            disabled={salvandoLeituras}
          >
            {salvandoLeituras ? "Salvando..." : "Salvar alterações"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={dialogTanqueAberto}
        onClose={() => !salvandoTanque && setDialogTanqueAberto(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{tanqueEditandoId ? "Editar Tanque" : "Novo Tanque"}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              label="Nome do tanque"
              value={tanqueForm.nome}
              onChange={(e) =>
                setTanqueForm((prev) => ({ ...prev, nome: e.target.value }))
              }
              fullWidth
            />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Temperatura mínima da faixa (°C)"
                type="number"
                value={tanqueForm.faixaMinima}
                onChange={(e) =>
                  setTanqueForm((prev) => ({ ...prev, faixaMinima: e.target.value }))
                }
                inputProps={{ step: "0.1" }}
                fullWidth
              />
              <TextField
                label="Temperatura máxima da faixa (°C)"
                type="number"
                value={tanqueForm.faixaMaxima}
                onChange={(e) =>
                  setTanqueForm((prev) => ({ ...prev, faixaMaxima: e.target.value }))
                }
                inputProps={{ step: "0.1" }}
                fullWidth
              />
            </Stack>

            <Typography variant="subtitle2" fontWeight={700}>
              Cálculo da quantidade de cal
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Comprimento do tanque (m)"
                type="number"
                value={tanqueForm.comprimento}
                onChange={(e) => setTanqueForm((prev) => ({ ...prev, comprimento: e.target.value }))}
                inputProps={{ min: "0", step: "0.01" }}
                fullWidth
              />
              <TextField
                label="Largura do tanque (m)"
                type="number"
                value={tanqueForm.largura}
                onChange={(e) => setTanqueForm((prev) => ({ ...prev, largura: e.target.value }))}
                inputProps={{ min: "0", step: "0.01" }}
                fullWidth
              />
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Altura da água (m)"
                type="number"
                value={tanqueForm.alturaAgua}
                onChange={(e) => setTanqueForm((prev) => ({ ...prev, alturaAgua: e.target.value }))}
                inputProps={{ min: "0", step: "0.01" }}
                fullWidth
              />
              <TextField
                label="Temperatura da água (°C)"
                type="number"
                value={tanqueForm.temperaturaAgua}
                onChange={(e) => setTanqueForm((prev) => ({ ...prev, temperaturaAgua: e.target.value }))}
                inputProps={{ min: "0", max: "100", step: "0.1" }}
                fullWidth
              />
            </Stack>

            {calculoCalTanque && calculoCalTanque.volumeM3 > 0 && (
              <Alert severity="info">
                Volume de água: {calculoCalTanque.volumeM3.toFixed(3)} m³. Quantidade teórica de cal: {calculoCalTanque.quantidadeCalKg.toFixed(2)} kg.
              </Alert>
            )}

            {!tanqueEditandoId ? (
              <>
                <Autocomplete
                  options={termometrosDisponiveis}
                  getOptionLabel={(option) => [option.nome, option.descricao].filter(Boolean).join(" - ")}
                  inputValue={buscaTermometro}
                  value={termometrosDisponiveis.find((item) => item.id === tanqueForm.termometroId) || null}
                  onInputChange={(event, novoTexto) => setBuscaTermometro(novoTexto)}
                  onChange={(event, novoValor) => {
                    setTanqueForm((prev) => ({ ...prev, termometroId: novoValor?.id || "" }));
                    setBuscaTermometro(novoValor?.nome || "");
                  }}
                  renderInput={(params) => <TextField {...params} label="Selecionar termômetro inicial" fullWidth />}
                  noOptionsText="Nenhum equipamento encontrado"
                />
                <TextField
                  label="Data de início do uso"
                  type="date"
                  value={tanqueForm.dataInicioTermometro}
                  onChange={(e) => setTanqueForm((prev) => ({ ...prev, dataInicioTermometro: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </>
            ) : (
              <>
                <Autocomplete
                  options={termometrosDisponiveis}
                  getOptionLabel={(option) => [option.nome, option.descricao].filter(Boolean).join(" - ")}
                  inputValue={buscaTermometro}
                  value={termometrosDisponiveis.find((item) => item.id === tanqueForm.termometroId) || null}
                  disabled={adicionandoTermometro}
                  onInputChange={(event, novoTexto) => setBuscaTermometro(novoTexto)}
                  onChange={(event, novoValor) => {
                    setTanqueForm((prev) => ({ ...prev, termometroId: novoValor?.id || "" }));
                    setBuscaTermometro(novoValor?.nome || "");
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Termohigrômetro atual"
                      helperText="Altere aqui somente para corrigir um equipamento selecionado por engano."
                      fullWidth
                    />
                  )}
                  noOptionsText="Nenhum equipamento encontrado"
                />
                {tanqueForm.dataInicioTermometro && (
                  <TextField
                    label="Em uso desde"
                    type="date"
                    value={tanqueForm.dataInicioTermometro}
                    InputLabelProps={{ shrink: true }}
                    disabled
                    fullWidth
                  />
                )}
                {!adicionandoTermometro ? (
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      const tanqueAtual = tanques.find((item) => item.id === tanqueEditandoId);
                      setAdicionandoTermometro(true);
                      setBuscaNovoTermometro("");
                      setBuscaTermometro(tanqueAtual?.termometroNome || "");
                      setTanqueForm((prev) => ({
                        ...prev,
                        termometroId: tanqueAtual?.termometroId || prev.termometroId,
                        novoTermometroId: "",
                        dataSubstituicao: dataAtual,
                      }));
                    }}
                    sx={{ alignSelf: "flex-start" }}
                  >
                    Acrescentar termohigrômetro
                  </Button>
                ) : (
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Stack spacing={2}>
                      <Typography variant="subtitle2" fontWeight={700}>Substituição do termohigrômetro</Typography>
                      <Autocomplete
                        options={termometrosDisponiveis.filter((item) => item.id !== tanqueForm.termometroId)}
                        getOptionLabel={(option) => [option.nome, option.descricao].filter(Boolean).join(" - ")}
                        inputValue={buscaNovoTermometro}
                        value={termometrosDisponiveis.find((item) => item.id === tanqueForm.novoTermometroId) || null}
                        onInputChange={(event, novoTexto) => setBuscaNovoTermometro(novoTexto)}
                        onChange={(event, novoValor) => {
                          setTanqueForm((prev) => ({ ...prev, novoTermometroId: novoValor?.id || "" }));
                          setBuscaNovoTermometro(novoValor?.nome || "");
                        }}
                        renderInput={(params) => <TextField {...params} label="Selecionar novo termohigrômetro" fullWidth />}
                        noOptionsText="Nenhum outro equipamento encontrado"
                      />
                      <TextField
                        label="Data da substituição"
                        type="date"
                        value={tanqueForm.dataSubstituicao}
                        onChange={(e) => setTanqueForm((prev) => ({ ...prev, dataSubstituicao: e.target.value }))}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                      />
                      <Button
                        onClick={() => {
                          setAdicionandoTermometro(false);
                          setBuscaNovoTermometro("");
                          setTanqueForm((prev) => ({ ...prev, novoTermometroId: "" }));
                        }}
                        sx={{ alignSelf: "flex-start" }}
                      >
                        Cancelar substituição
                      </Button>
                    </Stack>
                  </Paper>
                )}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          {tanqueEditandoId && (
            <Button
              variant="outlined"
              onClick={() => setDialogFormulasTanqueAberto(true)}
              disabled={!calculoCalTanque || calculoCalTanque.volumeM3 <= 0}
            >
              Ver fórmulas
            </Button>
          )}
          <Button onClick={() => setDialogTanqueAberto(false)} disabled={salvandoTanque}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={salvarTanque} disabled={salvandoTanque}>
            {salvandoTanque ? "Salvando..." : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={dialogFormulasTanqueAberto}
        onClose={() => setDialogFormulasTanqueAberto(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Planilha de verificação do cálculo</DialogTitle>
        <DialogContent dividers>
          {calculoCalTanque && (
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                Tanque: {tanqueForm.nome || "Sem nome"}
              </Typography>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Dado de entrada</strong></TableCell>
                    <TableCell align="right"><strong>Valor</strong></TableCell>
                    <TableCell><strong>Unidade</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>Comprimento do tanque</TableCell>
                    <TableCell align="right">{Number(tanqueForm.comprimento).toFixed(3)}</TableCell>
                    <TableCell>m</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Largura do tanque</TableCell>
                    <TableCell align="right">{Number(tanqueForm.largura).toFixed(3)}</TableCell>
                    <TableCell>m</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Altura da água</TableCell>
                    <TableCell align="right">{Number(tanqueForm.alturaAgua).toFixed(3)}</TableCell>
                    <TableCell>m</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Temperatura da água</TableCell>
                    <TableCell align="right">{Number(tanqueForm.temperaturaAgua).toFixed(1)}</TableCell>
                    <TableCell>°C</TableCell>
                  </TableRow>
                </TableBody>
              </Table>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Etapa</strong></TableCell>
                    <TableCell><strong>Fórmula</strong></TableCell>
                    <TableCell><strong>Substituição</strong></TableCell>
                    <TableCell align="right"><strong>Resultado</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>Volume de água</TableCell>
                    <TableCell>V = comprimento × largura × altura</TableCell>
                    <TableCell>
                      V = {Number(tanqueForm.comprimento).toFixed(3)} × {Number(tanqueForm.largura).toFixed(3)} × {Number(tanqueForm.alturaAgua).toFixed(3)}
                    </TableCell>
                    <TableCell align="right">{calculoCalTanque.volumeM3.toFixed(3)} m³</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Concentração da cal</TableCell>
                    <TableCell>C = (-0,0114 × T) + 1,850</TableCell>
                    <TableCell>
                      C = (-0,0114 × {Number(tanqueForm.temperaturaAgua).toFixed(1)}) + 1,850
                    </TableCell>
                    <TableCell align="right">{calculoCalTanque.concentracaoGL.toFixed(4)} g/L</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Quantidade de cal</TableCell>
                    <TableCell>Q = volume × concentração</TableCell>
                    <TableCell>
                      Q = {calculoCalTanque.volumeM3.toFixed(3)} × {calculoCalTanque.concentracaoGL.toFixed(4)}
                    </TableCell>
                    <TableCell align="right"><strong>{calculoCalTanque.quantidadeCalKg.toFixed(2)} kg</strong></TableCell>
                  </TableRow>
                </TableBody>
              </Table>

              <Alert severity="info">
                O resultado representa a quantidade teórica de cal para água saturada. A equivalência numérica usada é: 1 g/L em 1 m³ = 1 kg.
              </Alert>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogFormulasTanqueAberto(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={dialogLeituraAberto}
        onClose={() => !salvandoLeituras && setDialogLeituraAberto(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {grupoLeituraEmEdicao ? "Editar leituras" : "Cadastrar leituras"} -{" "}
          {tanqueSelecionado?.nome || ""}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              label="Data"
              type="date"
              size="small"
              value={formLeituras.data}
              onChange={(e) =>
                setFormLeituras((prev) => ({ ...prev, data: e.target.value }))
              }
              InputLabelProps={{ shrink: true }}
              sx={{ width: 180 }}
            />

            <Table
              size="small"
              sx={{
                border: "1px solid #d9dee7",
                "& th, & td": {
                  border: "1px solid #d9dee7",
                  py: 0.75,
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell align="center">HORÁRIO</TableCell>
                  <TableCell align="center">AMBIENTE (°C)</TableCell>
                  <TableCell align="center">TANQUE (°C)</TableCell>
                  <TableCell align="center">CONFORME</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {formLeituras.leituras.map((leitura, index) => (
                  <TableRow key={`popup-${index}`}>
                    <TableCell>
                      <TextField
                        type="time"
                        size="small"
                        value={leitura.horario}
                        onChange={(e) => atualizarLeitura(index, "horario", e.target.value)}
                        fullWidth
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={leitura.ambiente}
                        onChange={(e) => atualizarLeitura(index, "ambiente", e.target.value)}
                        fullWidth
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={leitura.tanque}
                        onChange={(e) => atualizarLeitura(index, "tanque", e.target.value)}
                        fullWidth
                      />
                    </TableCell>
                    {index === 0 && (
                      <TableCell rowSpan={3} align="center">
                        <SelecaoConforme
                          value={formLeituras.conforme}
                          onChange={() => {}}
                          disabled
                        />
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {formLeituras.conforme === false && (
              <CamposRegistroDesvio
                data={formLeituras.data}
                value={formLeituras.registroDesvio}
                onChange={(registroDesvio) =>
                  setFormLeituras((prev) => ({ ...prev, registroDesvio }))
                }
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogLeituraAberto(false)} disabled={salvandoLeituras}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={salvarLeituras} disabled={salvandoLeituras}>
            {salvandoLeituras
              ? "Salvando..."
              : grupoLeituraEmEdicao
                ? "Salvar alterações"
                : "Salvar leituras"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function CertificadoTermometroPaginas({ certificado }) {
  const [paginas, setPaginas] = useState([]);
  const [erro, setErro] = useState(false);

  useEffect(() => {
    let ativo = true;

    async function carregarPaginas() {
      setPaginas([]);
      setErro(false);
      const blob = await getBlob(ref(storage, certificado.storagePathAtual));
      const pdf = await pdfjsLib.getDocument({ data: await blob.arrayBuffer() }).promise;
      const imagens = [];

      for (let numero = 1; numero <= pdf.numPages; numero += 1) {
        const pagina = await pdf.getPage(numero);
        const viewport = pagina.getViewport({ scale: 1.8 });
        const canvas = document.createElement("canvas");
        const contexto = canvas.getContext("2d");
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        await pagina.render({ canvasContext: contexto, viewport }).promise;
        imagens.push(canvas.toDataURL("image/jpeg", 0.92));
        pagina.cleanup();
      }

      await pdf.destroy();
      if (ativo) setPaginas(imagens);
    }

    carregarPaginas().catch((error) => {
      console.error("Erro ao renderizar certificado do termômetro:", error);
      if (ativo) setErro(true);
    });

    return () => {
      ativo = false;
    };
  }, [certificado.storagePathAtual]);

  if (erro) {
    return <div className="tanques-print-page tanques-print-certificado-status">Não foi possível carregar o certificado.</div>;
  }

  if (paginas.length === 0) {
    return <div className="tanques-print-page tanques-print-certificado-status">Carregando certificado...</div>;
  }

  return paginas.map((imagem, index) => (
    <div className="tanques-print-page tanques-print-certificado" key={`${certificado.id}-${index}`}>
      <img src={imagem} alt={`Certificado de calibração - página ${index + 1}`} />
    </div>
  ));
}

function RelatorioTanquesCura({ tanques, onVoltar }) {
  function obterDadosMes(mes) {
    const [ano, numeroMes] = mes.split("-");
    const mesAnoExtenso = new Date(Number(ano), Number(numeroMes) - 1, 1)
      .toLocaleDateString("pt-BR", { month: "long", year: "numeric" })
      .replace(" de ", " DE ")
      .toUpperCase();
    const totalDias = new Date(Number(ano), Number(numeroMes), 0).getDate();
    const diasDoMes = Array.from({ length: totalDias }, (_, index) =>
      `${ano}-${numeroMes}-${String(index + 1).padStart(2, "0")}`
    );
    return { diasDoMes, mesAnoExtenso };
  }

  function linhasDoTanque(tanque, diasDoMes) {
    const gruposPorData = new Map(
      tanque.leiturasMes.map((grupo) => [grupo.data || grupo.id, grupo])
    );
    return diasDoMes.map((data) => {
      const grupo = gruposPorData.get(data);
      return {
        data,
        leituras: normalizarLeituras(grupo?.leituras),
        observacao: grupo?.observacao || "",
        conforme: typeof grupo?.conforme === "boolean" ? grupo.conforme : null,
        registroDesvio: normalizarRegistroDesvio(grupo?.registroDesvio),
      };
    });
  }

  return (
    <Box className="tanques-print-root" sx={{ p: 2, backgroundColor: "#eef1f5", minHeight: "100vh" }}>
      <style>{`
        @page tanquesCuraPrint { size: A4 portrait; margin: 0; }
        .tanques-print-page { page: tanquesCuraPrint; width: 210mm; height: 297mm; overflow: hidden; margin: 0 auto 8mm; padding: 12mm; box-sizing: border-box; background: #fff; color: #000; font-family: Arial, sans-serif; break-after: page; page-break-after: always; }
        .tanques-print-page:last-child { break-after: auto; page-break-after: auto; }
        .tanques-print-header { height: 18mm; display: grid; grid-template-columns: 32mm 1fr 36mm; border: 1px solid #000; margin-bottom: 3mm; box-sizing: border-box; }
        .tanques-print-logo { display: flex; align-items: center; justify-content: center; padding: 2mm; }
        .tanques-print-titulo { border-left: 1px solid #000; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 2mm; text-align: center; font-size: 9px; font-weight: 700; }
        .tanques-print-titulo-principal { font-size: 10px; }
        .tanques-print-identificacao { border-left: 1px solid #000; display: grid; grid-template-rows: repeat(3, 1fr); text-align: center; font-size: 9px; }
        .tanques-print-identificacao > div { display: flex; align-items: center; justify-content: center; }
        .tanques-print-identificacao > div + div { border-top: 1px solid #000; }
        .tanques-print-dados { height: 18mm; border: 1px solid #000; margin-bottom: 3mm; display: grid; grid-template-columns: 1fr 1fr; font-size: 9px; box-sizing: border-box; }
        .tanques-print-dados > div { display: flex; flex-direction: column; justify-content: space-evenly; padding: 1.5mm 3mm; }
        .tanques-print-dados > div + div { border-left: 1px solid #000; }
        .tanques-print-table, .tanques-print-desvios-table { width: 100%; border-collapse: collapse; table-layout: fixed; }
        .tanques-print-table { height: 163mm; font-size: 8px; }
        .tanques-print-table th, .tanques-print-table td { border: 1px solid #000; padding: .5mm; height: 5mm; line-height: 1; text-align: center; box-sizing: border-box; }
        .tanques-print-table th { height: 7mm; font-size: 8px; }
        .tanques-print-check { white-space: nowrap; }
        .tanques-print-check-box { display: inline-flex; align-items: center; justify-content: center; width: 3mm; height: 3mm; border: 1px solid #000; vertical-align: middle; margin: 0 .7mm 0 1.5mm; box-sizing: border-box; line-height: 1; }
        .tanques-print-desvios-titulo { margin: 6mm 0 2mm; font-size: 9px; font-weight: 700; }
        .tanques-print-desvios-table { font-size: 8px; }
        .tanques-print-desvios-table th, .tanques-print-desvios-table td { border: 1px solid #000; height: 6mm; padding: .8mm; text-align: center; vertical-align: middle; box-sizing: border-box; }
        .tanques-print-anexo-nota { margin-top: 3mm; font-size: 8px; font-weight: 700; }
        .tanques-print-certificado { padding: 0; }
        .tanques-print-certificado img { display: block; width: 210mm; height: 297mm; object-fit: contain; }
        .tanques-print-certificado-status { display: flex; align-items: center; justify-content: center; font-size: 14px; }
        @media print {
          .tanques-print-root { padding: 0 !important; min-height: auto !important; background: #fff !important; }
          .tanques-print-actions { display: none !important; }
          .tanques-print-page { width: 210mm; height: 297mm; margin: 0; padding: 12mm; }
          .tanques-print-page.tanques-print-certificado { padding: 0; }
          .tanques-print-page, .tanques-print-page * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
      <Stack className="tanques-print-actions" direction="row" spacing={1} justifyContent="flex-end" sx={{ mb: 2 }}>
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={onVoltar}>Voltar</Button>
        <Button variant="contained" startIcon={<PrintIcon />} onClick={() => window.print()}>Imprimir</Button>
      </Stack>
      {tanques.map((tanque) => {
        const { diasDoMes, mesAnoExtenso } = obterDadosMes(tanque.mesRelatorio);
        const linhas = linhasDoTanque(tanque, diasDoMes);
        const desvios = linhas.filter(
          (linha) => linha.conforme === false && linha.registroDesvio.desvio
        );
        const linhasDesvios = Array.from({ length: 4 }, (_, index) => desvios[index] || null);
        return (
          <div className="tanques-print-page" key={tanque.paginaId || tanque.id}>
            <div className="tanques-print-header">
              <div className="tanques-print-logo"><LogoRelatorio style={{ maxWidth: "27mm", maxHeight: "13mm" }} /></div>
              <div className="tanques-print-titulo">
                <div>SISTEMA DE GESTÃO DA QUALIDADE</div>
                <div className="tanques-print-titulo-principal">CONTROLE DE TEMPERATURA - TANQUE DE CURA DOS CORPOS DE PROVA NBR 9479</div>
              </div>
              <div className="tanques-print-identificacao">
                <div>RQ 4.2.1</div>
                <div>Revisão 00</div>
                <div>20/08/2025</div>
              </div>
            </div>

            <div className="tanques-print-dados">
              <div>
                <span><strong>TANQUE:</strong> {tanque.nome}</span>
                <span><strong>TERMÔMETRO:</strong> {tanque.nomesTermometros?.join(" / ") || tanque.termometroNome || "Não vinculado"}</span>
              </div>
              <div>
                <span><strong>MÊS/ANO:</strong> {mesAnoExtenso}</span>
                <span>
                  <strong>FAIXA:</strong>{" "}
                  {tanque.faixaMinima !== undefined && tanque.faixaMinima !== ""
                    ? formatarTemperatura(tanque.faixaMinima)
                    : "____"}{" "}
                  a{" "}
                  {tanque.faixaMaxima !== undefined && tanque.faixaMaxima !== ""
                    ? formatarTemperatura(tanque.faixaMaxima)
                    : "____"} °C
                </span>
              </div>
            </div>

            <table className="tanques-print-table">
              <colgroup>
                <col style={{ width: "12%" }} /><col style={{ width: "10%" }} /><col style={{ width: "13%" }} />
                <col style={{ width: "10%" }} /><col style={{ width: "13%" }} /><col style={{ width: "10%" }} />
                <col style={{ width: "13%" }} /><col style={{ width: "19%" }} />
              </colgroup>
              <thead><tr><th>DATA</th><th>HORA 1</th><th>TEMP. °C</th><th>HORA 2</th><th>TEMP. °C</th><th>HORA 3</th><th>TEMP. °C</th><th>CONFORME</th></tr></thead>
              <tbody>
                {linhas.map((linha) => (
                  <tr key={linha.data}>
                    <td>{formatarDataBR(linha.data)}</td>
                    {linha.leituras.map((leitura, index) => (
                      <React.Fragment key={`${linha.data}-${index}`}>
                        <td>{leitura.horario || "-"}</td>
                        <td>{leitura.tanque !== "" ? formatarTemperatura(leitura.tanque) : "-"}</td>
                      </React.Fragment>
                    ))}
                    <td className="tanques-print-check">
                      {typeof linha.conforme === "boolean" ? (
                        <>
                          <span className="tanques-print-check-box">{linha.conforme === true ? "X" : ""}</span>SIM
                          <span className="tanques-print-check-box">{linha.conforme === false ? "X" : ""}</span>NÃO
                        </>
                      ) : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="tanques-print-desvios-titulo">REGISTRO DE DESVIOS E AÇÕES TOMADAS</div>
            <table className="tanques-print-desvios-table">
              <colgroup><col style={{ width: "15%" }} /><col style={{ width: "35%" }} /><col style={{ width: "35%" }} /><col style={{ width: "15%" }} /></colgroup>
              <thead><tr><th>DATA</th><th>DESVIO</th><th>AÇÃO</th><th>RESP.</th></tr></thead>
              <tbody>
                {linhasDesvios.map((linha, index) => (
                  <tr key={index}>
                    <td>{linha ? formatarDataBR(linha.data) : "-"}</td>
                    <td>{linha?.registroDesvio.desvio || "-"}</td>
                    <td>{linha?.registroDesvio.acao || "-"}</td>
                    <td>{linha?.registroDesvio.responsavel || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="tanques-print-anexo-nota">
              * ANEXO 1: certificado de calibração termohigrômetros.
            </div>
          </div>
        );
      })}
      {tanques.flatMap((tanque) =>
        (tanque.certificados || []).map((certificado) => (
          <CertificadoTermometroPaginas
            certificado={certificado}
            key={`${tanque.id}-${certificado.id}`}
          />
        ))
      )}
    </Box>
  );
}
